import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Bell, Check, Plus, Trash2, Settings2, Loader2 } from 'lucide-react';

import { extractFieldErrors } from '@/shared/utils/errorUtils';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { channelSchema } from '@/feature/Rules/schemas/channelSchemas';
import useChannel from '@/feature/Rules/hooks/useChannel';

import BaseModal from '@/shared/components/modals/core/BaseModal';
import Input from '@/shared/components/elements/Input';
import Select from '@/shared/components/elements/Select';
import Button from '@/shared/components/elements/Button';

export default function ChannelModal({
  isOpen,
  onClose,
  onSubmit,
  channel = null,
  channelTypes = [],
  isLoading = false,
}) {
  const { isMobile } = useResponsive();

  const [serverErrors, setServerErrors] = useState({});
  const [selectedChannelTypeId, setSelectedChannelTypeId] = useState(null);
  const [dynamicFieldValues, setDynamicFieldValues] = useState({});
  const isEditMode = !!channel;

  const { channelTypeFields, isLoadingChannelTypeFields } = useChannel(
    '',
    null,
    1,
    10,
    null,
    selectedChannelTypeId,
    false,
    false,
  );

  const form = useForm({
    defaultValues: {
      name: channel?.name || '',
      channel_type_id: channel?.channel_type_id || '',
      is_active: channel?.is_active ?? true,
    },
    validatorAdapter: zodValidator(),
    onSubmit: async ({ value }) => {
      try {
        setServerErrors({});
        const finalExtraConfig = {};

        if (channelTypeFields?.data?.channel_type_fields) {
          channelTypeFields.data.channel_type_fields.forEach((field) => {
            const currentValue = dynamicFieldValues[field.name];

            if (Array.isArray(currentValue)) {
              const filteredArray = currentValue.filter(
                (val) => val && String(val).trim() !== '',
              );
              finalExtraConfig[field.name] = filteredArray;
            } else {
              finalExtraConfig[field.name] =
                currentValue !== undefined && currentValue !== ''
                  ? currentValue
                  : field.default_value || '';
            }
          });
        }

        const submitData = {
          name: value.name,
          channel_type_id: Number(value.channel_type_id),
          extra_config: finalExtraConfig,
        };

        if (isEditMode) {
          submitData.channelId = channel.id;
          submitData.is_active = value.is_active;
        }

        await onSubmit(submitData);
        form.reset();
        setDynamicFieldValues({});
        onClose();
      } catch (error) {
        console.error('Channel operation error:', error);
        const fieldErrors = extractFieldErrors(error);
        if (Object.keys(fieldErrors).length > 0) {
          setServerErrors(fieldErrors);
        }
      }
    },
  });

  useEffect(() => {
    if (channelTypeFields?.data?.channel_type_fields && isOpen) {
      const initialDynamicValues = {};

      channelTypeFields.data.channel_type_fields.forEach((field) => {
        const isListField =
          field.field_type === 'number_list' ||
          field.field_type === 'list' ||
          field.field_type === 'array' ||
          (isEditMode && Array.isArray(channel?.extra_config?.[field.name]));

        if (
          isEditMode &&
          channel?.extra_config &&
          channel.extra_config[field.name] !== undefined
        ) {
          const val = channel.extra_config[field.name];
          initialDynamicValues[field.name] = isListField
            ? Array.isArray(val) && val.length > 0
              ? val
              : ['']
            : val;
        } else {
          initialDynamicValues[field.name] = isListField
            ? ['']
            : field.default_value || '';
        }
      });

      setDynamicFieldValues(initialDynamicValues);
    }
  }, [channelTypeFields, isEditMode, channel, isOpen]);

  useEffect(() => {
    if (isOpen && channel) {
      form.setFieldValue('name', channel.name || '');
      form.setFieldValue('channel_type_id', channel.channel_type_id || '');
      form.setFieldValue('is_active', channel.is_active ?? true);
      setSelectedChannelTypeId(channel.channel_type_id);
    } else if (isOpen && !channel) {
      form.reset();
      setServerErrors({});
      setSelectedChannelTypeId(null);
      setDynamicFieldValues({});
    }
  }, [isOpen, channel, form]);

  const handleChannelTypeChange = (value) => {
    form.setFieldValue('channel_type_id', Number(value));
    setSelectedChannelTypeId(Number(value));
    setDynamicFieldValues({});
    if (serverErrors.channel_type_id) {
      setServerErrors((prev) => ({
        ...prev,
        channel_type_id: undefined,
      }));
    }
  };

  const handleDynamicFieldChange = useCallback(
    (fieldName, value, index = null) => {
      setDynamicFieldValues((prev) => {
        if (index !== null) {
          const newArray = [...(prev[fieldName] || [])];
          newArray[index] = value;
          return {
            ...prev,
            [fieldName]: newArray,
          };
        }
        return { ...prev, [fieldName]: value };
      });
    },
    [],
  );

  const addDynamicFieldItem = useCallback((fieldName) => {
    setDynamicFieldValues((prev) => ({
      ...prev,
      [fieldName]: [
        ...(Array.isArray(prev[fieldName]) ? prev[fieldName] : []),
        '',
      ],
    }));
  }, []);

  const removeDynamicFieldItem = useCallback((fieldName, index) => {
    setDynamicFieldValues((prev) => {
      const newArray = [...prev[fieldName]];
      newArray.splice(index, 1);
      return {
        ...prev,
        [fieldName]: newArray.length > 0 ? newArray : [''],
      };
    });
  }, []);

  const isArrayLikeField = (field, value) => {
    return (
      Array.isArray(value) ||
      field.field_type === 'number_list' ||
      field.field_type === 'list' ||
      field.field_type === 'array'
    );
  };

  const isNumericLikeField = (field) => {
    const type = String(field?.field_type || '').toLowerCase();
    const name = String(field?.name || '').toLowerCase();

    return (
      type.includes('number') ||
      type.includes('int') ||
      type.includes('float') ||
      name.includes('phone') ||
      name.includes('mobile') ||
      name.includes('number') ||
      name.includes('port') ||
      name.includes('timeout')
    );
  };

  const getDynamicPlaceholder = (field, index = null) => {
    const title = field?.title || 'مقدار';
    const base = field?.default_value?.trim() || title;

    if (index !== null) {
      return `${base} ${index + 1}`;
    }

    return base;
  };

  const getDynamicInputType = (field) => {
    return isNumericLikeField(field) ? 'text' : 'text';
  };

  const getDynamicInputDir = (field) => {
    return isNumericLikeField(field) ? 'ltr' : 'rtl';
  };

  const getDynamicInputClassName = (field) => {
    return isNumericLikeField(field) ? '!text-left' : '!text-right';
  };

  const renderDynamicField = (field) => {
    const value = dynamicFieldValues[field.name];
    const isArrayField = isArrayLikeField(field, value);
    const isNumericField = isNumericLikeField(field);

    if (isArrayField) {
      const listValues = Array.isArray(value) ? value : [''];

      return (
        <section
          key={field.id}
          className="bg-secondary-6 border-secondary-20 space-y-4 rounded-2xl border px-4 py-4 transition-all duration-300 sm:px-5 dark:border-gray-700/50 dark:bg-gray-800/40"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-tertiary-100 text-sm font-semibold dark:text-gray-100">
                {field.title}
                {field.is_required && (
                  <span className="text-error-100 mr-1">*</span>
                )}
              </h3>
              <p className="text-tertiary-70 mt-1 text-xs font-medium dark:text-gray-300">
                هر مقدار را در یک ردیف وارد کنید
              </p>
            </div>
            <span className="bg-secondary-20 border-secondary-40 text-secondary-100 shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300">
              چند مقداری
            </span>
          </div>

          <div className="space-y-3">
            {listValues.map((item, index) => (
              <div
                key={`${field.name}-${index}`}
                className="flex items-center gap-2.5"
              >
                <div className="border-tertiary-20 text-tertiary-80 flex h-[50px] min-w-[50px] items-center justify-center rounded-xl border bg-white text-sm font-semibold shadow-sm transition-colors dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-200">
                  {index + 1}
                </div>

                <div className="flex-1">
                  <Input
                    name={`${field.name}_${index}`}
                    type={getDynamicInputType(field)}
                    placeholder={getDynamicPlaceholder(field)}
                    value={item}
                    onChange={(e) =>
                      handleDynamicFieldChange(
                        field.name,
                        e.target.value,
                        index,
                      )
                    }
                    error={
                      index === 0
                        ? serverErrors[`extra_config.${field.name}`]
                        : undefined
                    }
                    disabled={isLoading}
                    dir={getDynamicInputDir(field)}
                    inputClassName={getDynamicInputClassName(field)}
                  />
                </div>

                {listValues.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="md"
                    onClick={() => removeDynamicFieldItem(field.name, index)}
                    disabled={isLoading}
                    className="text-error-100 hover:bg-error-100/10 hover:text-error-100 h-[50px] min-w-[50px] rounded-xl px-0 transition-colors dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300"
                    icon={<Trash2 size={18} />}
                    iconPosition="center"
                  />
                )}
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={() => addDynamicFieldItem(field.name)}
            disabled={isLoading}
            icon={<Plus size={16} />}
            iconPosition="left"
            className="border-tertiary-40 text-tertiary-80 hover:border-secondary-100 hover:bg-secondary-6 hover:text-secondary-100 w-full rounded-xl border-dashed transition-colors dark:border-gray-600 dark:text-gray-200 dark:hover:border-blue-500 dark:hover:bg-blue-500/10"
          >
            افزودن مقدار
          </Button>
        </section>
      );
    }

    return (
      <Input
        key={field.id}
        name={field.name}
        label={field.title}
        required={field.is_required}
        type={getDynamicInputType(field)}
        placeholder={getDynamicPlaceholder(field)}
        value={value !== undefined ? value : ''}
        onChange={(e) => handleDynamicFieldChange(field.name, e.target.value)}
        error={serverErrors[`extra_config.${field.name}`]}
        disabled={isLoading}
        dir={getDynamicInputDir(field)}
        inputClassName={getDynamicInputClassName(field)}
        icon={isNumericField ? undefined : <Settings2 size={16} />}
        iconPosition="right"
      />
    );
  };

  const channelTypeOptions = channelTypes.map((type) => ({
    value: type.id,
    label: type.title,
  }));

  const statusOptions = [
    { value: true, label: 'فعال' },
    { value: false, label: 'غیرفعال' },
  ];

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'ویرایش کانال' : 'افزودن کانال جدید'}
      icon={<Bell size={20} />}
      iconVariant="primary"
      size={isMobile ? 'full' : 'md'}
      contentClassName={isMobile ? 'max-h-[70vh] overflow-y-auto' : ''}
      closeOnBackdropClick={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onClose}
            disabled={isLoading}
            className="rounded-xl"
          >
            انصراف
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            icon={<Check size={18} />}
            iconPosition="left"
            className="rounded-xl"
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            {isEditMode ? 'ذخیره تغییرات' : 'افزودن کانال'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className={isMobile ? 'space-y-4 p-4' : 'space-y-5 p-6'}
      >
        <form.Field
          name="name"
          validators={{
            onChange: channelSchema.shape.name,
          }}
        >
          {(field) => {
            const fieldError = field.state.meta.errors?.[0];
            const errorMessage = fieldError
              ? typeof fieldError === 'string'
                ? fieldError
                : fieldError.message || 'خطای اعتبارسنجی'
              : serverErrors.name;

            return (
              <Input
                name="name"
                label="نام کانال"
                required={true}
                type="text"
                placeholder="نام کانال"
                value={field.state.value}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                  if (serverErrors.name) {
                    setServerErrors((prev) => ({
                      ...prev,
                      name: undefined,
                    }));
                  }
                }}
                error={errorMessage}
                icon={<Bell size={18} />}
                disabled={isLoading}
              />
            );
          }}
        </form.Field>

        <form.Field
          name="channel_type_id"
          validators={{
            onChange: channelSchema.shape.channel_type_id,
          }}
        >
          {(field) => {
            const fieldError = field.state.meta.errors?.[0];
            const errorMessage = fieldError
              ? typeof fieldError === 'string'
                ? fieldError
                : fieldError.message || 'خطای اعتبارسنجی'
              : serverErrors.channel_type_id;

            return (
              <Select
                name="channel_type_id"
                label="نوع کانال"
                required={true}
                value={field.state.value}
                onChange={handleChannelTypeChange}
                options={channelTypeOptions}
                error={errorMessage}
                disabled={isLoading}
                placeholder="نوع کانال را انتخاب کنید"
              />
            );
          }}
        </form.Field>

        {selectedChannelTypeId && (
          <div className="space-y-4">
            {isLoadingChannelTypeFields ? (
              <div className="border-tertiary-20 bg-secondary-6/50 text-tertiary-60 flex items-center justify-center gap-2 rounded-2xl border px-4 py-6 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
                <Loader2 size={18} className="animate-spin" />
                <p className="animate-pulse text-sm font-medium">
                  در حال بارگذاری تنظیمات اختصاصی...
                </p>
              </div>
            ) : (
              channelTypeFields?.data?.channel_type_fields?.map((field) =>
                renderDynamicField(field),
              )
            )}
          </div>
        )}

        {isEditMode && (
          <form.Field name="is_active">
            {(field) => (
              <Select
                name="is_active"
                label="وضعیت"
                required={true}
                value={field.state.value}
                onChange={(value) => {
                  field.handleChange(value === 'true' || value === true);
                }}
                options={statusOptions}
                disabled={isLoading}
                placeholder="وضعیت را انتخاب کنید"
              />
            )}
          </form.Field>
        )}
      </form>
    </BaseModal>
  );
}

ChannelModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  channel: PropTypes.object,
  channelTypes: PropTypes.array,
  isLoading: PropTypes.bool,
};
